import { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import {
  recoverPasswordSetNewPassword,
  recoverPasswordSendCode,
} from "../../redux/actions/session";

import logo from "../../assets/images/logo-trucoxo.png";

import "../login/Login.scss";

const PASSWORD_RULES = [
  { id: "len",     label: "8 caracteres",                    test: (s) => s.length >= 8 },
  { id: "upper",   label: "1 letra maiúscula",               test: (s) => /[A-Z]/.test(s) },
  { id: "lower",   label: "1 letra minúscula",               test: (s) => /[a-z]/.test(s) },
  { id: "special", label: "1 caractere especial (!@#$%¨&*)", test: (s) => /[!@#$%¨&*]/.test(s) },
  { id: "number",  label: "1 número",                        test: (s) => /\d/.test(s) },
];

const CODE_LENGTH = 6;

const RedefinirSenha = () => {
  const token = useSelector((state) => state.session.token);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [codeDigits, setCodeDigits] = useState(Array(CODE_LENGTH).fill(""));
  const [codeConfirmed, setCodeConfirmed] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const inputRefs = useRef([]);

  useEffect(() => {
    const queryParameters = new URLSearchParams(window.location.search);
    const emailFromQuery = queryParameters.get("email");
    if (emailFromQuery) setEmail(emailFromQuery);
  }, []);

  const code = codeDigits.join("");

  const passwordChecks = useMemo(
    () => PASSWORD_RULES.map((r) => ({ ...r, ok: r.test(password) })),
    [password]
  );
  const passwordValid = passwordChecks.every((c) => c.ok);

  const handleClose = () => navigate("/");

  const handleDigitChange = (index, value) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const next = [...codeDigits];
    next[index] = digit;
    setCodeDigits(next);
    if (digit && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleDigitKey = (index, e) => {
    if (e.key === "Backspace" && !codeDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleDigitPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, CODE_LENGTH);
    if (!pasted) return;
    const next = Array(CODE_LENGTH).fill("");
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
    setCodeDigits(next);
    const focusIdx = Math.min(pasted.length, CODE_LENGTH - 1);
    inputRefs.current[focusIdx]?.focus();
  };

  const handleVerifyCode = (e) => {
    e.preventDefault();
    if (code.length !== CODE_LENGTH) {
      return toast.error("Digite o código completo de 6 dígitos.");
    }
    setCodeConfirmed(true);
  };

  const handleResendCode = () => {
    if (!email) {
      return toast.error("E-mail não informado.");
    }
    dispatch(recoverPasswordSendCode(email));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!passwordValid) {
      return toast.error("Senha não atende aos critérios.");
    }
    if (password !== confirmPassword) {
      return toast.error("As senhas não conferem.");
    }
    dispatch(recoverPasswordSetNewPassword(email, code, password));
  };

  if (token) return null;

  return (
    <div className="login-page">
      <button
        type="button"
        className="login-page__close"
        onClick={handleClose}
        aria-label="Fechar"
      >
        <i className="fa-solid fa-xmark" /> Fechar
      </button>

      {!codeConfirmed ? (
        <form className="login-card" onSubmit={handleVerifyCode} noValidate>
          <div className="login-card__brand">
            <img src={logo} alt="Truco XP" />
          </div>

          <header className="login-card__header">
            <h1>Verifique seu e-mail</h1>
            <p>Digite o código de 6 dígitos que enviamos para o seu e-mail.</p>
          </header>

          <div className="code-input">
            {codeDigits.map((d, i) => (
              <input
                key={i}
                ref={(el) => (inputRefs.current[i] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={d}
                onChange={(e) => handleDigitChange(i, e.target.value)}
                onKeyDown={(e) => handleDigitKey(i, e)}
                onPaste={handleDigitPaste}
                className="code-input__digit"
                aria-label={`Dígito ${i + 1}`}
              />
            ))}
          </div>

          <button
            type="submit"
            className="login-submit"
            disabled={code.length !== CODE_LENGTH}
          >
            Verificar
          </button>

          <button
            type="button"
            className="login-card__signup-link"
            onClick={handleResendCode}
          >
            Não recebi o código
          </button>
        </form>
      ) : (
        <form className="login-card" onSubmit={handleSubmit} noValidate>
          <div className="login-card__brand">
            <img src={logo} alt="Truco XP" />
          </div>

          <header className="login-card__header">
            <h1>Crie sua nova senha</h1>
            <p>Siga todas as orientações de segurança</p>
          </header>

          <label className="login-field">
            <span className="login-field__label">Senha</span>
            <div className="login-field__input-wrap">
              <input
                type={showPassword ? "text" : "password"}
                className="login-field__input"
                placeholder="Digite sua senha"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="login-field__toggle"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
              >
                <i className={`fa-solid ${showPassword ? "fa-eye-slash" : "fa-eye"}`} />
              </button>
            </div>
          </label>

          {password.length > 0 && (
            <ul className="password-rules">
              {passwordChecks.map((rule) => (
                <li
                  key={rule.id}
                  className={`password-rules__item ${
                    rule.ok ? "password-rules__item--ok" : ""
                  }`}
                >
                  <span className="password-rules__icon" aria-hidden="true">
                    {rule.ok ? (
                      <i className="fa-solid fa-check" />
                    ) : (
                      <span className="password-rules__bullet" />
                    )}
                  </span>
                  <span className="password-rules__label">{rule.label}</span>
                </li>
              ))}
            </ul>
          )}

          <label className="login-field">
            <span className="login-field__label">Confirmar senha</span>
            <div className="login-field__input-wrap">
              <input
                type={showConfirm ? "text" : "password"}
                className="login-field__input"
                placeholder="Por segurança, confirme sua senha"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="login-field__toggle"
                onClick={() => setShowConfirm((v) => !v)}
                aria-label={showConfirm ? "Ocultar senha" : "Mostrar senha"}
              >
                <i className={`fa-solid ${showConfirm ? "fa-eye-slash" : "fa-eye"}`} />
              </button>
            </div>
          </label>

          <button
            type="submit"
            className="login-submit"
            disabled={!passwordValid || password !== confirmPassword}
          >
            Redefinir senha
          </button>
        </form>
      )}
    </div>
  );
};

export default RedefinirSenha;
